import React, {useRef, useState} from 'react';
import {Modal, Select, Input} from 'antd';
import {useCollection} from 'react-firebase-hooks/firestore';
import {collection, doc, getFirestore, query, serverTimestamp, setDoc, Timestamp, where} from 'firebase/firestore';
import {getDownloadURL, getStorage, ref as storageRef, uploadBytes} from 'firebase/storage';
import {useToast} from '@chakra-ui/react';
import Firebase from '../../Firebase/Firebase';

const db = getFirestore(Firebase);
const storage = getStorage(Firebase);

// Admin-side ad creation. Mirrors the rich seller form on the webapp (placement,
// link target, headline, priority tier, duration) but skips payment/approval —
// admin ads are written straight into the serving collection, live immediately.

type AdType = 'banner' | 'splash';

const TIERS = [
    {id: 'standard', label: 'Standard', weight: 3,  hint: 'Regular rotation'},
    {id: 'premium',  label: 'Premium',  weight: 6,  hint: '2× more likely to appear'},
    {id: 'dominant', label: 'Dominant', weight: 10, hint: '3× more likely to appear'},
];
const DURATIONS = [1, 3, 7, 14, 30];
type LinkTarget = 'none' | 'product' | 'shop' | 'external_url';

const genId = () =>
    Array(20).fill('ABCDEFGHIJKLMNOPQRSTUVWXYZ123456789abcdefghijklmnopqrstuvwxyz')
        .map(s => s[Math.floor(Math.random() * s.length)]).join('');

const chip = (active: boolean): React.CSSProperties => ({
    borderRadius: 20, padding: '5px 14px', fontSize: 13, cursor: 'pointer',
    border: active ? '1.5px solid #29327b' : '1.5px solid #e5e5e5',
    background: active ? '#29327b' : '#fff', color: active ? '#fff' : '#444',
    fontWeight: 600,
});

const AddAdModal = ({open, type, onClose}: {open: boolean; type: AdType; onClose: () => void}) => {
    const toast = useToast();
    const fileInput = useRef<HTMLInputElement>(null);
    const isBanner = type === 'banner';

    const [file, setFile] = useState<File | null>(null);
    const [preview, setPreview] = useState<string>('');
    const [saving, setSaving] = useState(false);

    const [placement, setPlacement]   = useState<'home' | 'category'>('home');
    const [linkTarget, setLinkTarget] = useState<LinkTarget>('none');
    const [linkValue, setLinkValue]   = useState('');       // product/shop id or url
    const [headline, setHeadline]     = useState('');
    const [tier, setTier]             = useState('standard');
    const [persistent, setPersistent] = useState(true);
    const [durationDays, setDurationDays] = useState(7);

    // Pickers (banner product/shop links)
    const [productSnap] = useCollection(collection(db, 'products'),
        {snapshotListenOptions: {includeMetadataChanges: true}});
    const [sellerSnap]  = useCollection(query(collection(db, 'users'), where('type', '==', 'Seller')),
        {snapshotListenOptions: {includeMetadataChanges: true}});
    const productOptions = (productSnap?.docs ?? []).map((d: any) => {
        const r = d.data();
        return {label: r.name ?? r.productId, value: r.productId};
    }).filter((o: any) => o.value);
    const sellerOptions = (sellerSnap?.docs ?? []).map((d: any) => {
        const r = d.data();
        return {label: r.fullName ?? r.Uid ?? r.sellerID, value: r.Uid ?? r.sellerID};
    }).filter((o: any) => o.value);

    const reset = () => {
        setFile(null); setPreview(''); setPlacement('home'); setLinkTarget('none');
        setLinkValue(''); setHeadline(''); setTier('standard'); setPersistent(true);
        setDurationDays(7);
    };

    const close = () => { reset(); onClose(); };

    const pickFile = (e: any) => {
        const f = e.target.files?.[0];
        if (f) { setFile(f); setPreview(URL.createObjectURL(f)); }
    };

    const save = async () => {
        if (!file) {
            toast({position:'top', title:`Please choose ${isBanner ? 'a banner' : 'a splash'} image`, status:'error', duration:2500, isClosable:true});
            return;
        }
        if (isBanner && linkTarget === 'external_url' && !linkValue.trim()) {
            toast({position:'top', title:'Enter the external URL', status:'error', duration:2500, isClosable:true});
            return;
        }
        if (isBanner && (linkTarget === 'product' || linkTarget === 'shop') && !linkValue) {
            toast({position:'top', title:`Select a ${linkTarget}`, status:'error', duration:2500, isClosable:true});
            return;
        }

        setSaving(true);
        const id = genId();
        const weight = TIERS.find(t => t.id === tier)?.weight ?? 3;
        const path = isBanner ? `adBanner/${id}` : `splashAds/${id}`;
        try {
            const up = await uploadBytes(storageRef(storage, path), file);
            const imageUrl = await getDownloadURL(up.ref);

            const endFields = persistent
                ? {isPersistent: true}
                : {isPersistent: false,
                   endDate: Timestamp.fromDate(new Date(Date.now() + durationDays * 86400000))};

            if (isBanner) {
                const linkType = linkTarget === 'shop' ? 'shop'
                    : linkTarget === 'external_url' ? 'url'
                    : linkTarget === 'product' ? 'product' : 'image';
                let value = '';
                if (linkTarget === 'external_url') {
                    const u = linkValue.trim();
                    value = u.startsWith('http') ? u : `https://${u}`;
                } else if (linkTarget === 'product' || linkTarget === 'shop') {
                    value = linkValue;
                }
                await setDoc(doc(db, 'banners', id), {
                    id,
                    imageUrl,
                    linkType,
                    linkValue: value,
                    isActive: true,
                    weight,
                    weightTier: tier,
                    placement,
                    ...(headline.trim() ? {headlineText: headline.trim()} : {}),
                    clickCount: 0,
                    impressionCount: 0,
                    ...endFields,
                    createdAt: serverTimestamp(),
                });
            } else {
                await setDoc(doc(db, 'splashAds', id), {
                    id,
                    imageUrl,
                    ...(headline.trim() ? {title: headline.trim()} : {}),
                    isActive: true,
                    weight,
                    weightTier: tier,
                    ...endFields,
                    createdAt: serverTimestamp(),
                });
            }

            toast({position:'top', title:`${isBanner ? 'Banner' : 'Splash ad'} added & live`, status:'success', duration:2500, isClosable:true});
            close();
        } catch (e: any) {
            toast({position:'top', title: e.message, status:'error', duration:3500, isClosable:true});
        } finally { setSaving(false); }
    };

    const ratioHint = isBanner ? '3:1 ratio — e.g. 1200 × 400 px' : '9:16 ratio — e.g. 1080 × 1920 px';
    const previewAspect = isBanner ? '3 / 1' : '9 / 16';
    const previewMaxW = isBanner ? '100%' : 220;

    return (
        <Modal
            title={isBanner ? 'Add Banner' : 'Add Splash Ad'}
            open={open}
            onOk={save}
            onCancel={close}
            okText={saving ? 'Saving…' : (isBanner ? 'Add Banner' : 'Add Splash Ad')}
            confirmLoading={saving}
            width={520}
        >
            <div style={{display:'flex', flexDirection:'column', gap:16, paddingTop:4}}>
                {/* Image */}
                <div>
                    <div style={{fontSize:13, fontWeight:600, marginBottom:6}}>
                        {isBanner ? 'Banner image' : 'Splash image'} <span style={{color:'#e53e3e'}}>*</span>
                    </div>
                    <div onClick={() => fileInput.current?.click()}
                         style={{aspectRatio:previewAspect, maxWidth:previewMaxW, width:'100%', cursor:'pointer',
                                 border:'2px dashed #d9d9d9', borderRadius:10, overflow:'hidden',
                                 display:'flex', alignItems:'center', justifyContent:'center',
                                 background:'#fafafa', color:'#aaa', fontSize:13}}>
                        {preview
                            ? <img src={preview} alt="preview" style={{width:'100%', height:'100%', objectFit:'cover'}} />
                            : <span>Click to upload</span>}
                    </div>
                    <div style={{fontSize:11, color:'#999', marginTop:4}}>{ratioHint}</div>
                    <input ref={fileInput} type="file" accept="image/*" style={{display:'none'}} onChange={pickFile} />
                </div>

                {/* Banner-only: placement */}
                {isBanner && (
                    <div>
                        <div style={{fontSize:13, fontWeight:600, marginBottom:6}}>Placement</div>
                        <div style={{display:'flex', gap:8}}>
                            <span style={chip(placement === 'home')}     onClick={() => setPlacement('home')}>Home page</span>
                            <span style={chip(placement === 'category')} onClick={() => setPlacement('category')}>Category page</span>
                        </div>
                    </div>
                )}

                {/* Banner-only: link target */}
                {isBanner && (
                    <div>
                        <div style={{fontSize:13, fontWeight:600, marginBottom:6}}>Links to</div>
                        <div style={{display:'flex', gap:8, flexWrap:'wrap'}}>
                            {(['none','product','shop','external_url'] as LinkTarget[]).map(t => (
                                <span key={t} style={chip(linkTarget === t)}
                                      onClick={() => { setLinkTarget(t); setLinkValue(''); }}>
                                    {t === 'none' ? 'Image only' : t === 'external_url' ? 'External URL'
                                        : t.charAt(0).toUpperCase() + t.slice(1)}
                                </span>
                            ))}
                        </div>
                        {linkTarget === 'product' && (
                            <Select showSearch optionFilterProp="label" placeholder="Select a product"
                                    style={{width:'100%', marginTop:8}} options={productOptions}
                                    value={linkValue || undefined} onChange={setLinkValue} />
                        )}
                        {linkTarget === 'shop' && (
                            <Select showSearch optionFilterProp="label" placeholder="Select a shop"
                                    style={{width:'100%', marginTop:8}} options={sellerOptions}
                                    value={linkValue || undefined} onChange={setLinkValue} />
                        )}
                        {linkTarget === 'external_url' && (
                            <Input placeholder="https://example.com" style={{marginTop:8}}
                                   value={linkValue} onChange={(e) => setLinkValue(e.target.value)} />
                        )}
                    </div>
                )}

                {/* Headline / ad text */}
                <div>
                    <div style={{fontSize:13, fontWeight:600, marginBottom:6}}>
                        {isBanner ? 'Headline (optional)' : 'Ad text (optional)'}
                    </div>
                    <Input maxLength={40} placeholder='e.g. "Flash Sale – 30% Off!"'
                           value={headline} onChange={(e) => setHeadline(e.target.value)} />
                </div>

                {/* Priority tier */}
                <div>
                    <div style={{fontSize:13, fontWeight:600, marginBottom:6}}>Priority</div>
                    <div style={{display:'flex', gap:8, flexWrap:'wrap'}}>
                        {TIERS.map(t => (
                            <span key={t.id} style={chip(tier === t.id)} title={t.hint} onClick={() => setTier(t.id)}>
                                {t.label}
                            </span>
                        ))}
                    </div>
                </div>

                {/* Schedule */}
                <div>
                    <div style={{fontSize:13, fontWeight:600, marginBottom:6}}>Schedule</div>
                    <div style={{display:'flex', gap:8, flexWrap:'wrap', alignItems:'center'}}>
                        <span style={chip(persistent)} onClick={() => setPersistent(true)}>No end date</span>
                        <span style={chip(!persistent)} onClick={() => setPersistent(false)}>Run for…</span>
                        {!persistent && DURATIONS.map(d => (
                            <span key={d} style={chip(durationDays === d)} onClick={() => setDurationDays(d)}>
                                {d}d
                            </span>
                        ))}
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default AddAdModal;
